import { useAudienceUploadLimit } from 'hooks/useAudienceUploadLimit'
import { useCampaignApproval } from 'hooks/useCampaignApproval'
import moment, { Moment } from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiCheckbox, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiSpacer, EuiTitle } from '@elastic/eui'

import { useGetAudienceFootprintSupportQuery } from 'api/rtkQueryApi/platform/organizationsApi'
import { showErrorToast, showSuccessToast, showWarningToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { AcFileUploader } from 'components/Basic/AcFileUploader'
import { AcTypeableDateField } from 'components/Basic/AcTypeableDateField'
import { DisplayAdUploader } from 'components/adCreation/DisplayAdUploader'
import { TvAdUploader } from 'components/adCreation/TvAdUploader'
import { useCampaignCreateSyncManager } from 'features/builders/createCampaign/useCampaignCreateSyncManager'
import history from 'services/HistoryService'
import { getUploadedAudienceFileName } from 'utils/uploadedAudienceFileName'
import { useDebounceEffect } from 'utils/useDebounceEffect'

import { useWhiteLabel } from '../../../whiteLabel/WhiteLabelContext'
import { FootprintFormRow } from './FootprintSelector'
import { FormStack } from './FormStack'
import { InternetDeliverySettings } from './InternetDeliverySettings'
import { QuickCampaignChannelPanel } from './QuickCampaignChannelPanel'
import { QuickCampaignResultsPanel } from './QuickCampaignResultsPanel'
import { quickCampaignChannelsReducer } from './quickCampaignChannelsReducer'
import { QuickCampaignChannel, QuickCampaignResult, QuickCampaignResultStatus, QuickCampaignShared, campaignNameFor, isAnyChannelUploading, isAudienceValid, missingItemsOf, newQuickCampaignChannels, newQuickCampaignShared, selectedChannelsOf } from './quickCampaignModels'
import { useQuickCampaignSubmit } from './useQuickCampaignSubmit'

const audienceCountDelayMilliseconds = 1000
const internetPrefix = `quickCampaign-${QuickCampaignChannel.Internet}`
const tvPrefix = `quickCampaign-${QuickCampaignChannel.Tv}`
const channelPanelStyle: React.CSSProperties = { flexBasis: 480, flexGrow: 1, maxWidth: 960 }

const countAudienceRows = (csv: string) => csv.split('\n').length - 1

export const QuickCampaignPage: React.FC = () => {
  const audienceUploadLimit = useAudienceUploadLimit()
  const [shared, setShared] = React.useState<QuickCampaignShared>(newQuickCampaignShared)
  const [channels, dispatchChannels] = React.useReducer(quickCampaignChannelsReducer, null, newQuickCampaignChannels)
  const [showErrors, setShowErrors] = React.useState<boolean>(false)
  const latestAudienceUrl = React.useRef<string>('')
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const whiteLabel = useWhiteLabel()
  const audienceFootprintSupportQuery = useGetAudienceFootprintSupportQuery()
  const audienceFootprintSupported = audienceFootprintSupportQuery.data?.isSupported ?? false
  const { isEnabled: campaignApprovalEnabled, canApprove } = useCampaignApproval()
  const { submit, isSubmitting, results } = useQuickCampaignSubmit(shared, channels)

  const dispatch = useDispatch()
  useCampaignCreateSyncManager()

  const updateShared = (changes: Partial<QuickCampaignShared>) => setShared(previous => ({ ...previous, ...changes }))

  const isSubmittedForApproval = campaignApprovalEnabled && !canApprove
  const selectedChannels = selectedChannelsOf(channels)
  const internetCampaignName = campaignNameFor(shared.campaignName, QuickCampaignChannel.Internet, channels)
  const tvCampaignName = campaignNameFor(shared.campaignName, QuickCampaignChannel.Tv, channels)

  useDebounceEffect(
    audienceCountDelayMilliseconds,
    () => {
      if (shared.isAudienceUploading) {
        return
      }
      if (shared.audienceUrl.length > 0) {
        countAudienceAddresses(shared.audienceUrl)
      } else {
        updateShared({ addressCount: 0, isAudienceCounting: false })
      }
    },
    [shared.audienceUrl, shared.isAudienceUploading]
  )

  const finishAudienceCount = (url: string, addressCount: number) => {
    if (latestAudienceUrl.current === url) {
      updateShared({ addressCount, isAudienceCounting: false })
    }
  }

  const countAudienceAddresses = (url: string) => {
    setTimeout(() => {
      const request = new XMLHttpRequest()
      request.open('GET', url, true)
      request.responseType = 'blob'
      request.onload = () => {
        if (request.status !== 200) {
          finishAudienceCount(url, 0)
          return
        }
        const reader = new FileReader()
        reader.onload = event => finishAudienceCount(url, countAudienceRows(event.target?.result as string))
        reader.onerror = () => finishAudienceCount(url, 0)
        reader.readAsText(request.response)
      }
      request.onerror = () => finishAudienceCount(url, 0)
      request.send()
    }, audienceCountDelayMilliseconds)
  }

  const onAudienceUrlChange = (url: string) => {
    latestAudienceUrl.current = url
    setShared(previous => ({ ...previous, audienceUrl: url, isAudienceCounting: url.length > 0, addressCount: url.length > 0 ? previous.addressCount : 0 }))
  }

  const landOnSingleCampaign = (result: QuickCampaignResult) => {
    if (result.status === QuickCampaignResultStatus.Failed || !result.campaignId) {
      dispatch(showErrorToast(result.message ?? 'Campaign could not be created'))
      return
    }
    if (result.status === QuickCampaignResultStatus.CreatedWithWarnings && result.message) {
      dispatch(showWarningToast(result.message))
    }
    if (isSubmittedForApproval) {
      dispatch(showSuccessToast('Your campaign was submitted for approval'))
    }
    history.push(`/campaigns/edit/${result.campaignId}`)
  }

  const missingItems = missingItemsOf(shared, channels)

  const onSubmitClick = async () => {
    if (missingItems.length > 0) {
      setShowErrors(true)
      return
    }
    const submitResults = await submit()
    if (submitResults.length === 1) {
      landOnSingleCampaign(submitResults[0])
    }
  }

  const isWaitingForUploads = isAnyChannelUploading(channels) || shared.isAudienceUploading || shared.isAudienceCounting
  const submitLabel = isWaitingForUploads ? 'Waiting for uploads' : selectedChannels.length > 1 ? 'Submit 2 campaigns to Ad Team' : 'Submit to Ad Team'
  const isNameMissing = showErrors && shared.campaignName.trim().length === 0
  const isChannelMissing = showErrors && selectedChannels.length === 0
  const isAudienceMissing = showErrors && !isAudienceValid(shared) && !shared.isAudienceCounting && !shared.isAudienceUploading
  const shouldShowMissingItems = showErrors && missingItems.length > 0
  const audienceFileName = getUploadedAudienceFileName(shared.audienceUrl)
  const campaignNamesHelpText = selectedChannels.length > 1 && shared.campaignName.trim().length > 0 ? `Creates ${internetCampaignName} and ${tvCampaignName}` : 'IP and TV are added to the name when both types are selected'

  if (results.length > 1) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <QuickCampaignResultsPanel results={results} isSubmittedForApproval={isSubmittedForApproval} />
        </EuiPageBody>
      </EuiPage>
    )
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>
                New Quick Campaign
                {whiteLabel?.isAgencies() && `: ${currentAccount?.name}`}
              </h1>
            </EuiTitle>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel hasBorder hasShadow={false} paddingSize='l' grow={false}>
          <EuiTitle size='s'>
            <h2>Campaign Details</h2>
          </EuiTitle>
          <EuiSpacer size='l' />
          <FormStack>
            <EuiFormRow id='quickCampaign-name' label='Campaign Name' helpText={campaignNamesHelpText} isInvalid={isNameMissing} error='Campaign name is required'>
              <EuiFieldText value={shared.campaignName} onChange={e => updateShared({ campaignName: e.target.value })} isInvalid={isNameMissing} />
            </EuiFormRow>
            <EuiFormRow id='quickCampaign-types' label='Campaign Types' hasChildLabel={false} isInvalid={isChannelMissing} error='Select at least one campaign type'>
              <EuiFlexGroup gutterSize='l' responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiCheckbox id={`${internetPrefix}-selected`} label='Internet' checked={channels.internet.isSelected} onChange={e => dispatchChannels({ type: 'setChannelSelected', channel: QuickCampaignChannel.Internet, isSelected: e.target.checked })} />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiCheckbox id={`${tvPrefix}-selected`} label='TV' checked={channels.tv.isSelected} onChange={e => dispatchChannels({ type: 'setChannelSelected', channel: QuickCampaignChannel.Tv, isSelected: e.target.checked })} />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFormRow>
            <EuiFlexGroup gutterSize='m' responsive={false}>
              <EuiFlexItem grow={false}>
                <AcTypeableDateField label='Start Date' value={shared.start} onChange={(start: Moment | null) => updateShared({ start })} minDate={moment()} showRequiredError={showErrors} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <AcTypeableDateField label='End Date' value={shared.end} onChange={(end: Moment | null) => updateShared({ end })} minDate={shared.start ?? moment()} showRequiredError={showErrors} />
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiFormRow label='Target Audience' isInvalid={isAudienceMissing} error='Upload an audience list'>
              <React.Fragment>
                <AcFileUploader maxFileSize={audienceUploadLimit.maxFileSize} allowedFileTypes={['text/plain', 'text/csv']} onUploadedUrlChange={onAudienceUrlChange} onIsValidFileChange={isValidUpload => updateShared({ isValidUpload })} onIsUploadingChange={isAudienceUploading => updateShared({ isAudienceUploading })} isLoading={shared.isAudienceCounting} />
                {shared.addressCount > 0 && !shared.isAudienceCounting && <EuiCallOut title={audienceFileName ? `${audienceFileName}: ${shared.addressCount} addresses` : `${shared.addressCount} addresses`} iconType='document' color='success' size='s' />}
                {!shared.isValidUpload && <EuiCallOut title={`Please upload a csv under ${audienceUploadLimit.label}`} iconType='document' color='warning' />}
              </React.Fragment>
            </EuiFormRow>
            {audienceFootprintSupported && <FootprintFormRow value={shared.footprint} onChange={footprint => updateShared({ footprint })} />}
          </FormStack>
        </EuiPanel>
        {selectedChannels.length > 0 && (
          <React.Fragment>
            <EuiSpacer size='l' />
            <EuiFlexGroup gutterSize='l' wrap>
              {channels.internet.isSelected && (
                <EuiFlexItem style={channelPanelStyle}>
                  <QuickCampaignChannelPanel
                    idPrefix={internetPrefix}
                    title='Internet'
                    campaignName={internetCampaignName}
                    budget={channels.internet.budget}
                    onBudgetChange={budget => dispatchChannels({ type: 'updateInternetFields', fields: { budget } })}
                    settings={<InternetDeliverySettings idPrefix={internetPrefix} internet={channels.internet} onChange={fields => dispatchChannels({ type: 'updateInternetFields', fields })} />}
                    creatives={
                      <DisplayAdUploader
                        idPrefix={internetPrefix}
                        draft={channels.internet}
                        showErrors={showErrors}
                        onImagesUploaded={files => dispatchChannels({ type: 'creativesUploaded', channel: QuickCampaignChannel.Internet, files })}
                        onImageRemove={url => dispatchChannels({ type: 'creativeRemoved', channel: QuickCampaignChannel.Internet, url })}
                        onIsUploadingChange={isUploading => dispatchChannels({ type: 'setUploading', channel: QuickCampaignChannel.Internet, isUploading })}
                        onModeChange={mode => dispatchChannels({ type: 'setLinkMode', channel: QuickCampaignChannel.Internet, mode })}
                        onSharedLinkChange={link => dispatchChannels({ type: 'setSharedLink', channel: QuickCampaignChannel.Internet, link })}
                        onCreativeLinkChange={(creativeUrl, link) => dispatchChannels({ type: 'setCreativeLink', channel: QuickCampaignChannel.Internet, creativeUrl, link })}
                      />
                    }
                  />
                </EuiFlexItem>
              )}
              {channels.tv.isSelected && (
                <EuiFlexItem style={channelPanelStyle}>
                  <QuickCampaignChannelPanel
                    idPrefix={tvPrefix}
                    title='TV'
                    campaignName={tvCampaignName}
                    budget={channels.tv.budget}
                    onBudgetChange={budget => dispatchChannels({ type: 'setTvBudget', budget })}
                    creatives={
                      <TvAdUploader
                        idPrefix={tvPrefix}
                        draft={channels.tv}
                        showErrors={showErrors}
                        onVideosUploaded={files => dispatchChannels({ type: 'creativesUploaded', channel: QuickCampaignChannel.Tv, files })}
                        onVideoRemove={url => dispatchChannels({ type: 'creativeRemoved', channel: QuickCampaignChannel.Tv, url })}
                        onIsUploadingChange={isUploading => dispatchChannels({ type: 'setUploading', channel: QuickCampaignChannel.Tv, isUploading })}
                        onSharedLinkChange={link => dispatchChannels({ type: 'setSharedLink', channel: QuickCampaignChannel.Tv, link })}
                      />
                    }
                  />
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </React.Fragment>
        )}
        <EuiSpacer size='xl' />
        {shouldShowMissingItems && (
          <React.Fragment>
            <EuiCallOut title='Complete these before submitting' color='danger' iconType='alert' size='s' style={{ maxWidth: 600 }}>
              <ul>
                {missingItems.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </EuiCallOut>
            <EuiSpacer size='m' />
          </React.Fragment>
        )}
        <EuiFlexGroup responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiButton fill size='m' iconType='check' onClick={onSubmitClick} isLoading={isSubmitting || isWaitingForUploads} isDisabled={isSubmitting || isWaitingForUploads}>
              {submitLabel}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size='xl' />
      </EuiPageBody>
    </EuiPage>
  )
}
